import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Upload, FileText, X, Clock, CheckCircle2 } from "lucide-react";
import { apiClient } from '../../api/HttpClient';

interface VerifyPropertyModalProps {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  onVerificationSuccess: () => void;
}

const VerifyPropertyModal: React.FC<VerifyPropertyModalProps> = ({ open, onClose, propertyId, onVerificationSuccess }) => {
  const [document, setDocument] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const propertyFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setDocument(e.target.files[0]);
    }
  };

  const handleDocumentTypeChange = (value: string) => {
    setDocumentType(value);
  };

  const handleSubmit = async () => {
    if (!document || !documentType) {
      alert('Please select a document and document type.');
      return;
    }

    const formData = new FormData();
    formData.append('document', document);
    formData.append('document_type', documentType);
    formData.append('property', String(propertyId));

    try {
      const client = await apiClient;
      await client.post(`${process.env.REACT_APP_USER_SERVICE_API_ENDPOINT}/api/hosts/property-verifications/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Property verification document submitted successfully!');
      onVerificationSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting property verification:', error);
      alert('Failed to submit property verification document.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Verify Property</DialogTitle>
          <DialogDescription>
            Upload documents to verify your property.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <>
            {/* Document Type Select */}
            <Select
              value={documentType}
              onValueChange={handleDocumentTypeChange}
            >
              <SelectTrigger className="w-full bg-background">
                <SelectValue placeholder="Document Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ownership_deed">Property Ownership Deed</SelectItem>
                <SelectItem value="rental_agreement">Rental Agreement</SelectItem>
                <SelectItem value="utility_bill">Utility Bill</SelectItem>
                <SelectItem value="tax_receipt">Property Tax Receipt</SelectItem>
                <SelectItem value="registration">Property Registration</SelectItem>
              </SelectContent>
            </Select>

            {/* File Upload */}
            <input
              type="file"
              ref={propertyFileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
            />
            <Button
              variant="outline"
              className="w-full border-dashed border-2 h-12"
              onClick={() => propertyFileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {document ? document.name : "CHOOSE FILE"}
            </Button>
            {document && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-2">
                <FileText className="h-4 w-4 flex-shrink-0" />
                <span className="truncate flex-1">{document.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1 text-destructive hover:text-destructive"
                  onClick={() => {
                    setDocument(null);
                    if (propertyFileInputRef.current) propertyFileInputRef.current.value = "";
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </>
        </div>

        <DialogFooter className="flex-row justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={onClose}
          >
            CANCEL
          </Button>
          <Button
            className="bg-primary hover:bg-primary/90"
            disabled={!documentType || !document}
            onClick={handleSubmit}
          >
            SUBMIT
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VerifyPropertyModal;
